import { atom } from 'nanostores';
import { MUSIC_TRACKS } from '../assets/music';

export interface MusicState {
  currentTrack: string | null;
  isPlaying: boolean;
  volume: number;
  isFading: boolean;
}

export const musicState = atom<MusicState>({
  currentTrack: null,
  isPlaying: false,
  volume: 0.5,
  isFading: false
});

let currentAudio: HTMLAudioElement | null = null;
let fadeInterval: NodeJS.Timeout | null = null;
// Флаг для предотвращения одновременного запуска нескольких треков
let isPlayingTrack = false;
// Очередь для управления переходами
let transitionQueue: Array<{trackName: TrackName, resolve: () => void}> = [];
let isProcessingQueue = false;

export type TrackName = keyof typeof MUSIC_TRACKS;

// Функция для плавного затухания
const fadeOut = (audio: HTMLAudioElement, duration: number = 2000): Promise<void> => {
  return new Promise((resolve) => {
    const startVolume = audio.volume;
    const fadeStep = startVolume / (duration / 100);
    
    const fade = () => {
      if (audio.volume > 0) {
        audio.volume = Math.max(0, audio.volume - fadeStep);
        setTimeout(fade, 100);
      } else {
        audio.pause();
        audio.currentTime = 0;
        resolve();
      }
    };
    
    fade();
  });
};

// Функция для плавного появления
const fadeIn = (audio: HTMLAudioElement, targetVolume: number, duration: number = 2000): Promise<void> => {
  return new Promise((resolve) => {
    audio.volume = 0;
    const fadeStep = targetVolume / (duration / 100);
    
    const fade = () => {
      if (audio.volume < targetVolume) {
        audio.volume = Math.min(targetVolume, audio.volume + fadeStep);
        setTimeout(fade, 100);
      } else {
        resolve();
      }
    };
    
    fade();
  });
};

// Функция для обработки очереди переходов
const processQueue = async (): Promise<void> => {
  if (isProcessingQueue || transitionQueue.length === 0) {
    return;
  }
  
  isProcessingQueue = true;
  
  while (transitionQueue.length > 0) {
    const { trackName, resolve } = transitionQueue.shift()!;
    
    try {
      await playTrackInternal(trackName);
      resolve();
    } catch (error) {
      console.error('Ошибка в очереди переходов:', error);
      resolve();
    }
  }
  
  isProcessingQueue = false;
};

// Внутренняя функция воспроизведения трека
const playTrackInternal = async (trackName: TrackName): Promise<void> => {
  const state = musicState.get();
  
  console.log('🎵 playTrackInternal:', trackName, 'текущий трек:', state.currentTrack, 'играет:', state.isPlaying);
  
  // Если уже играет тот же трек, ничего не делаем
  if (state.currentTrack === trackName && state.isPlaying) {
    console.log('🎵 Тот же трек уже играет, пропускаем');
    return;
  }
  
  // Сначала полностью останавливаем текущий трек
  if (currentAudio) {
    console.log('🎵 Останавливаем текущий трек');
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
  
  // Обновляем состояние
  musicState.set({
    currentTrack: null,
    isPlaying: false,
    isFading: false,
    volume: state.volume
  });
  
  // Создаем новый аудио элемент
  const audio = new Audio(MUSIC_TRACKS[trackName]);
  currentAudio = audio;
  
  // Настраиваем аудио
  audio.loop = true;
  audio.volume = 0; // Начинаем с нуля для fade-in
  
  // Обработчики событий c защитой от двойного старта
  let isStarted = false;
  const startOnce = async () => {
    if (isStarted) return;
    if (currentAudio !== audio) return;
    isStarted = true;
    try {
      console.log('🎵 Начинаем воспроизведение трека:', trackName);
      await audio.play();
      await fadeIn(audio, state.volume);
      musicState.set({
        currentTrack: trackName,
        isPlaying: true,
        isFading: false,
        volume: state.volume
      });
      console.log('🎵 Трек успешно запущен:', trackName);
    } catch (error) {
      console.error('Ошибка воспроизведения:', error);
      if (error instanceof Error && error.name !== 'NotAllowedError') {
        musicState.set({
          currentTrack: null,
          isPlaying: false,
          isFading: false,
          volume: state.volume
        });
      }
    }
  };
  audio.addEventListener('canplaythrough', startOnce, { once: true } as any);
  audio.addEventListener('canplay', startOnce, { once: true } as any);
  
  audio.addEventListener('error', (e) => {
    console.error('Ошибка загрузки трека:', trackName, e);
    musicState.set({
      currentTrack: null,
      isPlaying: false,
      isFading: false,
      volume: state.volume
    });
  });
  
  // Загружаем аудио
  audio.load();
};

// Основная функция воспроизведения трека
export const playTrack = async (trackName: TrackName): Promise<void> => {
  console.log('🎵 playTrack вызван:', trackName);
  
  return new Promise((resolve) => {
    // Добавляем в очередь
    transitionQueue.push({ trackName, resolve });
    
    // Запускаем обработку очереди
    processQueue();
  });
};

// Функция остановки музыки
export const stopMusic = async (): Promise<void> => {
  // Очищаем очередь
  transitionQueue = [];
  isProcessingQueue = false;
  
  if (currentAudio) {
    const state = musicState.get();
    musicState.set({ ...state, isFading: true });
    await fadeOut(currentAudio);
    currentAudio = null;
    musicState.set({
      currentTrack: null,
      isPlaying: false,
      isFading: false,
      volume: state.volume
    });
  }
};

// Функция изменения громкости
export const setVolume = (volume: number): void => {
  const state = musicState.get();
  musicState.set({ ...state, volume: Math.max(0, Math.min(1, volume)) });
  
  if (currentAudio) {
    currentAudio.volume = volume;
  }
};

// Функция паузы/возобновления
export const togglePlayPause = (): void => {
  if (currentAudio) {
    const state = musicState.get();
    if (state.isPlaying) {
      currentAudio.pause();
      musicState.set({ ...state, isPlaying: false });
    } else {
      currentAudio.play();
      musicState.set({ ...state, isPlaying: true });
    }
  }
};
